import FuseUtils from '@fuse/utils';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import _ from '@lodash';
import { showMessage } from 'app/store/fuse/messageSlice';
import AuraTable from 'app/main/apps/common/AuraTable';
import withRouter from '@fuse/core/withRouter';
import { useLocation } from 'react-router-dom';
import { getUpdateStatusValues, setOrdersSearchText, setResetFilters } from '../store/ordersSlice';
import { checkNullOrUndefined, getParams, isCustomer, mapParams } from '../../common/AuraFunctions';
import { addComments } from '../store/orderSlice';
import { statusFilterNamesCancelled } from 'app/main/apps/e-commerce/shared/CustomerFilter';

function EntitiesContent(props) {
  const {
    isManage,
    role,
    entitiesSelector,
    getEntities,
    getColumns,
    selectEntities,
    getEntityStatuses,
    listType,
    statusCategory,
    handleSearchText,
    filterType,
    includeInvoice,
    includeArchive,
    includeCancelled,
    // PGSS-5371: destructured rather than read off props behind a typeof guard. The first version
    // guarded it, Entities.js did not forward it, and the guard turned a missing prop into a
    // checkbox that simply never ticked.
    setIncludeCancelled = () => {},
    includeWaitingApproval,
    showOnlyVAS,
    vasTypes,
    type,
    entitiesFor,
    headerTitle,
    reorderDate,
    actionableOnly,
    blockFetchStatuses
  } = props;

  const dispatch = useDispatch();
  const entities = useSelector(selectEntities);
  const searchText = useSelector(({ eCommerceApp }) => eCommerceApp[entitiesSelector].searchText);
  const customer = useSelector(({ eCommerceApp }) => eCommerceApp[entitiesSelector].customer);
  const supplier = useSelector(({ eCommerceApp }) => eCommerceApp.orders.supplier);
  const resetFilter = useSelector(({ eCommerceApp }) => eCommerceApp.orders.resetFilter);
  const salesPerson = useSelector(({ eCommerceApp }) => eCommerceApp.orders.salesPerson);
  const listLoading = useSelector(({ eCommerceApp }) => eCommerceApp[entitiesSelector].loading);
  const [filteredData, setFilteredData] = useState(null);
  const toolbarParam = { pageName: entitiesSelector, selectionMsg: `${listType} Selected`, options: filterType, stage: type };
  const [headers, setHeaders] = useState({});
  const [params, setParams] = useState({});
  const { orderListHeaders } = useSelector(({ eCommerceApp }) => eCommerceApp[entitiesSelector]);
  const { selectedValue } = useSelector(({ eCommerceApp }) => eCommerceApp?.orders);
  const [showNoDataMessage, setShowNoDataMessage] = useState(null);
  const [pageNoUpdated, setPageNoUpdated] = useState(false);
  const isSupplierInvoice = entitiesFor && entitiesFor === 'supplier-invoices';
  const ref = useRef()
  const finalParam = {
    ...params,
    [props.type === 'approval' ? 'title' : 'stage']: props.type === 'approval' ? 'proof' : props.type,
    ...(isSupplierInvoice ? { is_supplier: true } : {}),
    ...(props?.tabType ? { viewType: props.tabType } : {}),
    // PGSS-5371: carry the flag on the request itself, from the prop. The fetch effect below
    // rebuilds the URL from finalParam and replaceState()s it, so anything written to the URL by
    // another effect in the same tick is overwritten and never reaches the API.
    ...(includeCancelled ? { include_cancelled: true } : {})
  }
  const shouldBlockFetchStatuses = blockFetchStatuses ?? false;

  useEffect(() => {
    return () => {
      // following code will run on unmount.
      setShowNoDataMessage(null)
    };
  }, []);

  // Added for Global search
  useEffect(() => {
    const { orderNo, invoiceNo } = props.searchParams;
    // When parent detects orderNo, invoice_no change in the URL,
    // it passes the new value down as a prop.
    // We sync that value into local param state to prepare for API call.
    // Added entitiesFor and undefined conditiones to avoid accidental update
    if(!checkNullOrUndefined(orderNo) && ["rfqs", "quotes", "orders"].includes(entitiesFor)) {
      setParams(prev => ({ ...prev, "orderNo": orderNo }))
    }
    if(!checkNullOrUndefined(invoiceNo) && ["invoices", "supplier-invoices"].includes(entitiesFor)) {
      setParams(prev => ({ ...prev, "invoice_no": invoiceNo }))
    }
}, [props.searchParams?.orderNo, props.searchParams?.invoiceNo]);



  const onPageChange = (value) => {
    setPageNoUpdated(true);
    setHeaders(prev => ({ ...prev, "App-Pagination-Num": value }))
  };

  const onPerPageChange = (value) => {
    setHeaders(prev => ({ ...prev, "App-Pagination-Limit": value }))
  };

  const onSorting = (key, desc) => {
    const sortvalue = `${key},${desc ? 'DESC' : 'ASC'}`
    if (key)
      setHeaders(prev => ({ ...prev, "App-Pagination-Sort": sortvalue }))
    else {
      const temp = { ...headers, "App-Pagination-Sort": desc };
      // delete temp['App-Pagination-Sort']
      setHeaders(temp)
    }
  }

  const serverPaginationProps = {
    changePage: onPageChange,
    perPage: onPerPageChange,
  };


  const debounceFn = useCallback(
    debounce((header, param) => {
      setShowNoDataMessage(null)
      dispatch(getEntities({
        params: {
          ...param,
        }, headers: header
      }))
        .unwrap()
        .then((response) => {
          if (response?.length <= 0) {
            setShowNoDataMessage(true)
          }
        })
    }, 500),
    []
  );

  const updateParams = () => {
    const computedParams = getParams()
    setHeaders(computedParams.headerObj)
    setParams(computedParams.filterObj)
  }

  useEffect(() => {
    if (ref.current) {
      setHeaders({});
      setParams({});
      setShowNoDataMessage(null)
    }
  }, [entitiesFor])

  useEffect(() => {
    updateParams()
    setShowNoDataMessage(null)
  }, [customer, supplier, salesPerson, filterType, selectedValue, includeInvoice, includeArchive, includeCancelled, includeWaitingApproval, showOnlyVAS, vasTypes, reorderDate,actionableOnly]) // to enable hook for change of these filter values

  /**
   * PGSS-5371: keep Include Cancelled Invoices in step with the Status column filter.
   *
   * Cancelled invoices are opt-in via the checkbox. Narrowing the Status filter to the cancelled
   * status is a request to see them, so the box is ticked to match — otherwise the screen could
   * contradict itself, box unchecked and the list showing only cancelled invoices.
   *
   * This only moves the checkbox. The request carries include_cancelled off `includeCancelled` in
   * finalParam above, so the API is correct even on the render before this lands.
   *
   * One-way on purpose: unticking the box stays the user's, and the Status filter is theirs to
   * clear. It can only ever widen scope, never silently hide rows.
   */
  useEffect(() => {
    if (includeCancelled || !statusFilterNamesCancelled(params?.invoice_status)) {
      return;
    }
    setIncludeCancelled(true);
  }, [params?.invoice_status, includeCancelled, setIncludeCancelled])

  useEffect(() => {
    // Whenever params (including orderNo) or dependencies change,
    // we debounce and fire the API call.
    // This allows reacting to both initial load and query param changes without remounting.
    if (ref.current) {
      let removeKey = ['stage', 'title']
      if(pageNoUpdated === false) {
        removeKey = ['stage', 'title', 'pageNo']
      }
      setPageNoUpdated(false)
      const searchParams = new URLSearchParams(mapParams({ ...finalParam, ...headers }, removeKey));
      // replacestate function will not rerender the page
      if (searchParams.toString())
        window.history.replaceState(null, "", `${window.location.href.split('?')[0]}?${searchParams.toString()}`);
    }
    else updateParams()
    debounceFn(headers, finalParam)
    ref.current = true
    setShowNoDataMessage(null)
  }, [dispatch, debounceFn, headers, params, props.searchParamOrderNo]);

  useEffect(() => {
    if (resetFilter) {
      updateParams()
      debounceFn(headers, finalParam)
      dispatch(setResetFilters(false))
    }
  }, [debounceFn, headers, params, resetFilter])

  useEffect(() => {
    let param = { c: statusCategory };
    // added below condition to prevent duplicate API calls on filter change
    if (shouldBlockFetchStatuses === false) {
       //  added this condition to set my_action param when actionableOnly is true and user is manage and type is rfq,quote,order
      if (
        actionableOnly === true &&
        isManage &&
        ["rfq", "quote", "order"].includes(type)
      ) {
        param = { ...param, my_action: true };
      }
      if (isCustomer(role) && ["quote", "approval"].includes(type)) {
        param = { ...param, scopeToWaitingApproval: true };
      }
      // PGSS-5352: with the option on, the dropdown must offer the waiting-for-approval statuses too,
      // so the newly visible rows can be filtered like any other. The exclusion is applied client-side
      // in ordersSlice off this param, so not sending it is what lifts it.
      if (isCustomer(role) && type === "order" && !includeWaitingApproval) {
        param = { ...param, excludeWaitingApproval: true };
      }
      dispatch(
        getEntityStatuses({
          params: param,
        })
      );
    }

    if (props.type === "order") {
      dispatch(getUpdateStatusValues({ c: statusCategory }));
    }
    // PGSS-5352: includeWaitingApproval belongs here too — without it the dropdown keeps the
    // status list it was built with and the newly visible rows cannot be filtered.
  }, [dispatch, statusCategory, props.entitiesFor, actionableOnly, includeWaitingApproval]);

  useEffect(() => {
    function getFilteredArray(_entities, _searchText) {
      if (_searchText.length === 0) {
        return _entities;
      }
      return FuseUtils.filterArrayByString(_entities, _searchText);
    }

    if (entities) {
      setFilteredData(getFilteredArray(entities, searchText));
    }
  }, [entities, searchText]);

  // When our cell renderer calls updateMyData, we'll use
  // the rowIndex, columnId and new value to update the
  // original data
  const updateMyData = (data, value, updateFor) => {
    if(updateFor === "customer_internal_status") {
      const tempData = {};
      tempData.order_id = data?.original?.id;
      tempData.comments = value;
      dispatch(addComments(tempData)).then((res) => {
        if(!res.error) {
          dispatch(
            showMessage({
              message: "Internal status added successfully",
              variant: 'success',
              anchorOrigin: {
                vertical: 'top',
                horizontal: 'right',
              },
            })
          );
        }
      })
    }
  };

  return (
    <div className="flex-auto p-16 w-full">
      <AuraTable
        columns={getColumns(isManage, customer, role)}
        data={entities}
        resetFilters={props.resetFilters}
        isManage={props.isManage}
        toolbarParam={toolbarParam}
        headers={orderListHeaders}
        serverPaginationProps={serverPaginationProps}
        params={params}
        updateParams={(key, value) => setParams(prev => ({ ...prev, [key]: value }))}
        onSorting={onSorting}
        loading={listLoading}
        showListInfo={showNoDataMessage && showNoDataMessage === true}
        loaderProps={{
          show: !filteredData || filteredData.length === 0,
          loading: listLoading,
          message: `There are no ${listType}!`
        }}
        loaderWithSpace
        noDataMessage={`There are no ${_.lowerCase(headerTitle)}!`}
        cellData={props.cellData}
        updateMyData={updateMyData}
      />
    </div>
  );
}
export default withRouter(EntitiesContent);
